import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { VIEW, ADD, EDIT, DELETE } from "const"
import { deepCopy } from "client/utils"
import {
    StoreType,
    addModal,
    updateLifecyclePermission,
    setLifecyclePermissionsAccountGroupSelected,
    setLifecyclePermissionsProjectGroupSelected,
    setLifecyclePermissionsUserSelected,
} from "client/store"
import {
    CommonPermissionRow,
    CommonPermissionItem,
    PermissionType,
} from "client/store/utils"
import {
    PermissionTableColumn,
    PermissionList,
    PermissionListItem,
    PermissionListAddItem,
    PermissionLabel,
    Permission,
    DeletePermissionButton,
} from "client/components"

import NotAssigned from "./notAssigned"

const Columns = (editAccess: boolean) => {
    const accountGroupList = useSelector((state: StoreType) => state.group.groupList)
    const projectGroupList = useSelector((state: StoreType) => state.projectGroup.groupList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const permissionFormatter = (
        section: string,
        permissionId: string,
        permissionType: PermissionType,
        permission: CommonPermissionRow,
    ) => {
        const permissionItem = permission[permissionType]
        if (!permissionItem) return null // unused permission

        const { accountGroups, projectGroups, users } = permissionItem

        return (
            <PermissionList>
                {
                    accountGroups.map((groupId) => {
                        const group = accountGroupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    editAccess && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: CommonPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.accountGroups = accountGroups.filter((id) => id !== groupId)
                                                dispatch(updateLifecyclePermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    project.id
                    && projectGroups.map((groupId) => {
                        const group = projectGroupList[groupId]
                        return group && (
                            <PermissionListItem key={groupId}>
                                <PermissionLabel>{`${lang.group}:`}</PermissionLabel>
                                <Permission>{group.name}</Permission>
                                {
                                    editAccess && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: CommonPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.projectGroups = projectGroups.filter((id) => id !== groupId)
                                                dispatch(updateLifecyclePermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    users.map((userId) => {
                        const user = userList[userId]
                        return user && (
                            <PermissionListItem key={userId}>
                                <PermissionLabel>{`${lang.user}:`}</PermissionLabel>
                                <Permission>{`${user.firstName} ${user.lastName}`}</Permission>
                                {
                                    editAccess && (
                                        <DeletePermissionButton
                                            onClick={() => {
                                                const newPermissionItem: CommonPermissionItem = deepCopy(permissionItem)
                                                newPermissionItem.users = users.filter((id) => id !== userId)
                                                dispatch(updateLifecyclePermission(
                                                    section,
                                                    permissionId,
                                                    permissionType,
                                                    newPermissionItem,
                                                ))
                                            }}
                                        />
                                    )
                                }
                            </PermissionListItem>
                        )
                    })
                }
                {
                    editAccess && (
                        <PermissionListAddItem
                            onClick={() => {
                                dispatch(setLifecyclePermissionsAccountGroupSelected(null))
                                dispatch(setLifecyclePermissionsProjectGroupSelected(null))
                                dispatch(setLifecyclePermissionsUserSelected(null))
                                dispatch(addModal({
                                    key: "notAssignedPermissions",
                                    content: <NotAssigned
                                        section={section}
                                        permissionId={permissionId}
                                        permissionType={permissionType}
                                        permissionItem={permissionItem}
                                    />,
                                }))
                            }}
                        >
                            {`+ ${lang.add}`}
                        </PermissionListAddItem>
                    )
                }
            </PermissionList>
        )
    }

    const cellMinWidth = "200px"
    const columns: PermissionTableColumn[] = [
        {
            field: "name",
            header: lang.name,
            cellMinWidth,
            cellMaxWidth: "300px",
            formatter: (_section, _permissionId, permission: CommonPermissionRow) => permission.title,
        },
        {
            field: VIEW,
            header: lang.view,
            cellMinWidth,
            formatter: (section, permissionId, permission: CommonPermissionRow) => (
                permissionFormatter(section, permissionId, VIEW, permission)
            ),
        },
        {
            field: ADD,
            header: lang.add,
            cellMinWidth,
            formatter: (section, permissionId, permission: CommonPermissionRow) => (
                permissionFormatter(section, permissionId, ADD, permission)
            ),
        },
        {
            field: EDIT,
            header: lang.edit,
            cellMinWidth,
            formatter: (section, permissionId, permission: CommonPermissionRow) => (
                permissionFormatter(section, permissionId, EDIT, permission)
            ),
        },
        {
            field: DELETE,
            header: lang.delete,
            cellMinWidth,
            formatter: (section, permissionId, permission: CommonPermissionRow) => (
                permissionFormatter(section, permissionId, DELETE, permission)
            ),
        },
    ]

    return columns
}
export default Columns
